/**
 * Sorting function for difficulty levels
 * Order: Easy < Medium < Hard < Very Hard
 */
export const sortDifficulty = (rowA, rowB, columnId) => {
  const difficultyOrder = {
    Easy: 1,
    Medium: 2,
    Hard: 3,
    'Very Hard': 4,
  };

  const getDifficultyValue = (row) => {
    const difficulty = row.original.difficulty?.title ?? '';
    return difficultyOrder[difficulty] ?? 0; // Default to 0 for unknown difficulties
  };

  const valueA = getDifficultyValue(rowA);
  const valueB = getDifficultyValue(rowB);

  return valueA - valueB;
};

export const likelihoodOrder = {
  'Highly Likely': 1,
  Likely: 2,
  'Less Likely': 3,
  Certain: 4,
  Rarely: 5,
  Never: 6,
};

/**
 * Generic sorting function for numerical values
 * Handles both numbers and strings that can be parsed to numbers
 */
export const sortNumerical = (rowA, rowB, columnId) => {
  const getValue = (row) => {
    const value = row.original[columnId];

    // Handle null/undefined values
    if (value == null) return 0;

    // If it's already a number, return it
    if (typeof value === 'number') return value;

    // If it's a string, try to parse it
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }

    // Default to 0 for any other type
    return 0;
  };

  const valueA = getValue(rowA);
  const valueB = getValue(rowB);

  return valueA - valueB;
};

/**
 * Creates a numerical sorting function for a specific field path
 * Use this when the column name doesn't match the data field name
 */
export const createNumericalSort = (fieldPath) => {
  return (rowA, rowB, columnId) => {
    const getValue = (row) => {
      const value = row.original[fieldPath];

      // Handle null/undefined values
      if (value == null) return 0;

      // If it's already a number, return it
      if (typeof value === 'number') return value;

      // If it's a string, try to parse it
      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
      }

      // Default to 0 for any other type
      return 0;
    };

    const valueA = getValue(rowA);
    const valueB = getValue(rowB);

    return valueA - valueB;
  };
};

/**
 * Sorting function for modules
 * Takes an array of module names in the desired sort order
 */
export const sortedModules = (sortOrder, key = 'module') => {
  return (rowA, rowB, columnId) => {
    const getModuleValue = (row) => {
      const module = row.original[key]?.title ?? '';
      const index = sortOrder.indexOf(module);
      return index !== -1 ? index : sortOrder.length; // Unknown modules go to the end
    };

    const valueA = getModuleValue(rowA);
    const valueB = getModuleValue(rowB);

    return valueA - valueB;
  };
};

/**
 * Creates a grouped sorting function that groups items by a specific field before sorting
 * Items with the same group value will be kept together, then sorted within their group
 */
export const createGroupedSort = (fieldPath) => {
  return (rowA, rowB, columnId) => {
    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    };

    const getGroupValue = (row) => {
      const value = getNestedValue(row.original, fieldPath);

      // Handle null/undefined values
      if (value == null) return '';

      // Convert to string for grouping
      return String(value);
    };

    const getSortValue = (row) => {
      // For the sort value, we want to use the actual column data
      // The columnId corresponds to the header title, so we need to get the right field
      const value = getNestedValue(row.original, fieldPath);

      // Handle null/undefined values
      if (value == null) return '';

      // Convert to string for sorting
      return String(value).toLowerCase();
    };

    const groupA = getGroupValue(rowA);
    const groupB = getGroupValue(rowB);

    // First sort by group
    if (groupA !== groupB) {
      return groupA.localeCompare(groupB);
    }

    // If in the same group, sort by the column value (which is the same as group value for this case)
    const valueA = getSortValue(rowA);
    const valueB = getSortValue(rowB);

    return valueA.localeCompare(valueB);
  };
};

/**
 * Creates a sorting function for dynamic fields (arrays of cells with whatToShow)
 * @param {string} fieldName - The value of whatToShow to match (e.g., 'module', 'difficulty')
 * @param {Function} compareFn - Comparison function that takes two values and returns -1, 0, or 1
 * @param {string} dynamicFieldKey - The key in row.original containing the dynamic field array
 * @returns {Function} A sorting function compatible with table sorting
 */
export const sortDynamicField = (fieldName, compareFn, dynamicFieldKey) => {
  return (rowA, rowB, columnId) => {
    const getValue = (row) => {
      const dynamicField = row.original[dynamicFieldKey];

      if (!Array.isArray(dynamicField)) return null;

      const cell = dynamicField.find((c) => c?.whatToShow === fieldName);
      return cell?.[fieldName] ?? null;
    };

    const valueA = getValue(rowA);
    const valueB = getValue(rowB);

    return compareFn(valueA, valueB);
  };
};

/**
 * Extracts unique values from nested data structures for filtering
 * Handles the complex aLevel data structure with column arrays
 */
export const extractFilterValues = (rows, extractor) => {
  const values = new Set();

  rows.forEach((row) => {
    const value = extractor(row);
    if (value !== null && value !== undefined && value !== '') {
      values.add(value);
    }
  });

  return Array.from(values);
};

/**
 * Module extractor for aLevel tables
 * Looks for module data in the row structure
 */
export const extractModuleFromALevelRow = (row) => {
  // For DatoCmsEdexcelALevelTopic, the module is directly available
  if (row.module && row.module.title) {
    return row.module.title;
  }

  // Fallback: Check column arrays for other table types (like ALevel Further)
  const allColumnKeys = Object.keys(row).filter((key) => key.toLowerCase().includes('column'));

  for (const columnName of allColumnKeys) {
    const columnData = row[columnName];
    if (Array.isArray(columnData)) {
      for (const cell of columnData) {
        if (cell && cell.whatToShow === 'module' && cell.module) {
          return cell.module;
        }
      }
    }
  }

  return null;
};

/**
 * Chapter extractor for aLevel tables
 * Looks for textbook chapter data
 */
export const extractChapterFromALevelRow = (row) => {
  // For DatoCmsEdexcelALevelTopic, the chapter is directly available
  if (row.chapter) {
    const chapterNum = parseInt(row.chapter);
    if (!isNaN(chapterNum)) {
      return chapterNum;
    }
  }

  // Fallback: Check for other chapter field names
  if (row['text-bookchapter']) {
    const chapterNum = parseInt(row['text-bookchapter']);
    if (!isNaN(chapterNum)) {
      return chapterNum;
    }
  }

  return null;
};

/**
 * Grade extractor for GCSE tables
 * Extracts grade from edexcelGcseColumnTwo (column_two in Dato)
 */
export const extractGradeFromGcseRow = (row) => {
  // For DatoCmsEdexcelGcseTopic, the grade is in edexcelGcseColumnTwo
  if (row.edexcelGcseColumnTwo !== null && row.edexcelGcseColumnTwo !== undefined) {
    const grade = parseInt(row.edexcelGcseColumnTwo);
    if (!isNaN(grade)) {
      return grade;
    }
  }

  return null;
};
